import { useTranslation } from "react-i18next";
import {
  AsyncCallArgs,
  GridContainer,
  GridItem,
  handleSubmitValidationFail,
  InputFormField,
  SelectFormField,
  NomenclatureAutocompleteFormField,
  isNotEmpty,
  useAsyncCall,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import { ViewDialog, CircularLoader } from "@duosoftbg/nacid-components";
import { FormProvider, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import React, { useEffect, useState } from "react";

import { Alert, TextField } from "@mui/material";
import useAppSelector from "../../../../../hooks/redux/base/useAppSelector";
import { referenceDataDomainsThunk } from "../../../../../store/redux/slice/AppData/referenceDataDomains";
import useAppDispatch from "../../../../../hooks/redux/base/useAppDispatch";
import { closeModal } from "../../../../../store/redux/slice/ComponentsControl/referenceDataControl";
import { createReferenceDataValidationSchema } from "../../../../../yup/schema/referenceData/referenceDataValidationSchema";
import { ReferenceDataDetails } from "../../../../../types/referenceDataTypes";
import { referenceDataInitialValues } from "../../../../../init/referenceDataInitialValues";
import { createReferenceData, getReferenceData, updateReferenceData } from "../../../../../axios/api/services";

const Content = ({ loading, error, methods, id, domain }) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();

  const options = [
    { value: true, text: t("l.yes") },
    { value: false, text: t("l.no") },
  ];

  const thunkState = useAppSelector((state) => {
    return state.AppData.referenceDataDomains;
  });

  if (loading) {
    return <CircularLoader />;
  }

  if (error) {
    return <Alert severity="error">{t("m.error.serverFetchingError")}</Alert>;
  }

  return (
    <FormProvider {...methods}>
      <form>
        <GridContainer spacing={3}>
          <GridItem sm={3} md={3}>
            {id !== "" && domain !== "" && (
              <TextField
                id="domain"
                label={t("l.reference.data.domain")}
                size={"small"}
                value={t(getValues("domain"))}
                disabled={true}
                style={{ width: "100%" }}
              />
            )}
            {!(id !== "" && domain !== "") && (
              <NomenclatureAutocompleteFormField
                required={true}
                fieldName={"domain"}
                labelCode={"l.reference.data.domain"}
                thunkFn={referenceDataDomainsThunk}
                thunkState={thunkState}
              />
            )}
          </GridItem>
          <GridItem sm={3} md={3}>
            <InputFormField
              required={true}
              fieldName={"id"}
              labelCode={"l.nomenclature.code"}
              isDisabled={id !== "" && domain !== ""}
            />
          </GridItem>
          <GridItem sm={3} md={3}>
            <InputFormField type={"number"} required={true} fieldName={"index"} labelCode={"l.nomenclature.index"} />
          </GridItem>
          <GridItem sm={3} md={3}>
            <SelectFormField
              required={true}
              fieldName={"isActive"}
              labelCode={"l.nomenclature.active"}
              addEmptyOption={false}
              selectOptions={options}
            />
          </GridItem>
          <GridItem sm={12} md={12}>
            <InputFormField required={true} fieldName={"name"} labelCode={"l.nomenclature.name"} />
          </GridItem>
        </GridContainer>
      </form>
    </FormProvider>
  );
};

const SaveReferenceDataDialog = ({ filterFn }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { open, id, domain } = useAppSelector((state) => {
    return state.ComponentsControl.referenceDataControl.modals.edit;
  });

  const handleCloseDialog = () => {
    dispatch(closeModal({ modalType: "edit" }));
  };

  const { methods } = useReactHookForm<ReferenceDataDetails>({
    defaultValues: referenceDataInitialValues,
    validationSchema: createReferenceDataValidationSchema,
  });

  useEffect(() => {
    if (open) {
      if (isNotEmpty(id) && isNotEmpty(domain)) {
        const asyncCallArgs: AsyncCallArgs = {
          promise: getReferenceData(domain, id),
          onSuccess: (response) => {
            methods.reset(response);
            setLoading(false);
          },
          onError: () => {
            setError(true);
            setLoading(false);
          },
        };
        asyncCall(asyncCallArgs);
      } else {
        methods.reset(referenceDataInitialValues);
        setLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [id, domain, open]);

  const onSubmit = (values) => {
    const asyncCreation: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: id === "" && domain === "" ? createReferenceData(values) : updateReferenceData(values),
      reactHooksForm: { methods },
      onSuccess: () => {
        methods.reset(referenceDataInitialValues, {
          keepIsSubmitted: true,
          keepSubmitCount: true,
        });
        toast.success(t("m.create.nomenclature.success"));
        handleCloseDialog();
        filterFn();
      },
    };
    asyncCall(asyncCreation);
  };

  return (
    <ViewDialog
      open={open}
      onClose={handleCloseDialog}
      title={id === "" && domain === "" ? "t.nomenclature.create.dialog" : id && domain && "t.nomenclature.edit.dialog"}
      disableEnforceFocus
      dialogActionsSpacing={{ pr: 3 }}
      onSubmitBtnClick={
        !loading && !error && methods.handleSubmit(onSubmit, () => handleSubmitValidationFail(methods.formState, t))
      }
    >
      <Content methods={methods} id={id} domain={domain} loading={loading} error={error} />
    </ViewDialog>
  );
};

export default SaveReferenceDataDialog;
